import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, Loader2, LifeBuoy, BookOpen, Mail } from "lucide-react";

const SUPPORT_EMAIL = "support@urbis.shop";

export default function Support() {
  const { lang } = useLang();
  const es = lang === "es";
  const [sid, setSid] = useState("");
  const [msgs, setMsgs] = useState([{ role: "assistant", content: es ? "¡Hola! Soy el asistente de URBIS. ¿En qué puedo ayudarte? Puedo explicarte planes, facturación, cobros online, sucursales y más." : "Hi! I'm the URBIS assistant. How can I help? I can explain plans, invoicing, online payments, branches and more." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const suggestions = es
    ? ["¿Cómo cobro una factura online?", "¿Qué es el Add-on Multi-Sucursal?", "¿Cómo cambio mi plan?", "¿Quién paga la comisión de la tarjeta?"]
    : ["How do I collect an invoice online?", "What is the Multi-Branch add-on?", "How do I change my plan?", "Who pays the card fee?"];

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/support/chat", { message: q, session_id: sid, lang });
      setSid(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", content: formatApiErrorDetail(err.response?.data?.detail) }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="support-page">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-primary/10 text-primary"><LifeBuoy className="h-6 w-6" /></span>
        <div>
          <h1 className="font-head text-3xl font-extrabold text-secondary sm:text-4xl">{es ? "Centro de Soporte" : "Support Center"}</h1>
          <p className="text-sm text-slate-500">{es ? "Pregúntale a nuestro asistente con IA o escríbenos." : "Ask our AI assistant or contact us."}</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/manual" className="inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-secondary hover:border-primary hover:text-primary" data-testid="support-manual-link"><BookOpen className="h-4 w-4" /> {es ? "Manual de usuario" : "User manual"}</Link>
        <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-secondary hover:border-primary hover:text-primary" data-testid="support-email-link"><Mail className="h-4 w-4" /> {SUPPORT_EMAIL}</a>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 bg-secondary px-5 py-3 text-white">
          <Bot className="h-5 w-5 text-primary" /> <span className="font-head font-bold">{es ? "Asistente URBIS" : "URBIS Assistant"}</span>
        </div>
        <div className="h-[420px] space-y-4 overflow-y-auto p-5" data-testid="support-messages">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`support-msg-${m.role}`}>{m.content}</div>
            </div>
          ))}
          {loading && <div className="flex justify-start"><div className="rounded-2xl bg-slate-100 px-4 py-2.5 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
          <div ref={endRef} />
        </div>
        {msgs.length <= 1 && (
          <div className="flex flex-wrap gap-2 px-5 pb-3">
            {suggestions.map((s) => (
              <button key={s} onClick={() => send(s)} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200" data-testid="support-suggestion">{s}</button>
            ))}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-slate-100 p-3">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={es ? "Escribe tu pregunta…" : "Type your question…"} className="rounded-sm" data-testid="support-input" />
          <Button type="submit" disabled={loading || !input.trim()} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="support-send"><Send className="h-4 w-4" /></Button>
        </form>
      </div>
      <p className="mt-3 text-center text-xs text-slate-400">{es ? "Si el asistente no resuelve tu duda, escríbenos a " : "If the assistant can't help, email us at "}<a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary">{SUPPORT_EMAIL}</a></p>
    </div>
  );
}
